import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { AIReview } from "common/models/types";
import { parsePromptSections } from "../../../utils/promptSectionParser";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { openReportInNewWindow } from "../../../utils/PrintUtil";
import { Button, Flex } from "components/atoms";
import { COLOR_SYSTEM } from "components/design/design-system";
import { copyPatient } from "../../../api/patientService";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { useNavigate } from "react-router-dom";
import { fetchUsers } from "../../../api/userService";
import Input from "components/atoms/Input";

// 고정 사용자 목록 (자주 사용하는 계정)
const PRESET_USERS = [
  { id: "e4e84d3c-7051-7006-667c-4228e03a2076", name: "서울대 연구실", type: "institution" },
  { id: "d4980dec-7021-706b-7c6e-e1935e2a285f", name: "JR", type: "admin" },
  { id: "0488cd2c-20a1-704d-4715-7aabc3b98328", name: "Oscar", type: "admin" },
  { id: "5438edec-2011-705b-c22f-d1167e08f732", name: "James", type: "admin" },
  { id: "a4a81dcc-70b1-706e-f448-8127816bddb9", name: "Genie", type: "admin" },
];

// 스타일 컴포넌트
const UserSelectModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const UserSelectContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 400px;
  max-height: 80vh;
  overflow-y: auto;
`;

const UserSelectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const UserSelectTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};

  &:hover {
    color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  }
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  margin-bottom: 8px;
  margin-top: 16px;
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserItem = styled.button<{ $isInstitution?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: ${({ $isInstitution }) => $isInstitution ? COLOR_SYSTEM.get("Blue")?.[50] : "white"};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[100]};
    border-color: ${COLOR_SYSTEM.get("Blue")?.[300]};
  }
`;

const UserName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const UserType = styled.span<{ $type: string }>`
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${({ $type }) =>
    $type === "institution" ? COLOR_SYSTEM.get("Blue")?.[100] : COLOR_SYSTEM.get("Gray")?.[100]};
  color: ${({ $type }) =>
    $type === "institution" ? COLOR_SYSTEM.get("Blue")?.[600] : COLOR_SYSTEM.get("Gray")?.[600]};
`;

const SearchResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 8px;
`;

export interface AIReviewDetailTemplateProps {
  review: AIReview;
  onDeleteReview?: () => void;
  onRetryLLM?: () => void;
  onRefresh?: () => void;
}

const TAB_IDS = {
  REQUEST: "request",
  PATIENT_SNAP: "patientSnap",
  REFERENCE_SNAP: "referenceSnap",
  ANALYSIS: "analysis",
  ACTUAL_PROMPT: "actualPrompt",
  AI_RESPONSE: "aiResponse",
  REASONING_LOG: "reasoningLog",
} as const;

type TabId = (typeof TAB_IDS)[keyof typeof TAB_IDS];

const copyToClipboard = async (text: string, successMessage: string, failureMessage: string) => {
  try {
    await navigator.clipboard.writeText(text);
    ToastManager.show({ content: successMessage, duration: 2000 });
  } catch (err) {
    console.error("Failed to copy: ", err);
    ToastManager.show({ content: failureMessage, duration: 3000 });
  }
};

const AIReviewDetailAdminViewerTemplate: React.FC<AIReviewDetailTemplateProps> = ({
  review,
  onDeleteReview,
  onRetryLLM,
  onRefresh,
}) => {
  const { request, patientSnap, referenceSnap, analysis, actualPrompt, reviewResult, reasoningLog } = review;
  const [activeTab, setActiveTab] = useState<TabId | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [showUserSelectModal, setShowUserSelectModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Array<{ id: string; name: string; email?: string; license?: string }>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();

  // 실제 프롬프트를 시스템/환자·처방/허가사항/가이드라인/항콜린 섹션으로 분할
  const promptSections = useMemo(
    () => parsePromptSections(actualPrompt || ""),
    [actualPrompt]
  );

  const toggleSection = (key: string, isOpen: boolean) =>
    setExpandedSections((prev) => ({ ...prev, [key]: !isOpen }));

  // 사용자 검색
  const handleSearchUsers = async (query: string) => {
    setSearchQuery(query);
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetchUsers({ limit: 100 });
      if (response.data) {
        const filtered = response.data.filter(
          (user) =>
            user.name?.toLowerCase().includes(query.toLowerCase()) ||
            user.email?.toLowerCase().includes(query.toLowerCase()) ||
            user.license?.includes(query)
        );
        setSearchResults(
          filtered.map((u) => ({
            id: u.id,
            name: u.name || "",
            email: u.email,
            license: u.license,
          }))
        );
      }
    } catch (error) {
      console.error("Failed to search users:", error);
    } finally {
      setIsSearching(false);
    }
  };

  // 특정 사용자에게 환자 복사
  const handleCopyToSpecificUser = async (targetUserId: string, targetUserName: string) => {
    if (!patientSnap) {
      ToastManager.show({ content: "환자 정보가 없습니다." });
      return;
    }

    setShowUserSelectModal(false);

    ConfirmManager.show({
      title: "환자 정보 복사",
      body: `이 환자의 정보를 "${targetUserName}" 계정으로 복사하시겠습니까?`,
      buttons: [
        <Button
          key="cancel"
          size="Medium"
          type="Text"
          variant="Outlined"
          color="Secondary"
          onClick={() => ConfirmManager.hide()}
        >
          취소
        </Button>,
        <Button
          key="confirm"
          size="Medium"
          type="Text"
          variant="Contained"
          color="Primary"
          onClick={async () => {
            ConfirmManager.hide();
            try {
              WaitingManager.show({ message: `"${targetUserName}" 계정으로 환자 정보를 복사하는 중...` });

              const result = await copyPatient(patientSnap, targetUserId);

              if (!result.error && result.data) {
                ToastManager.show({
                  content: `환자 정보가 "${targetUserName}" 계정으로 성공적으로 복사되었습니다.`,
                });
              } else {
                ToastManager.show({ content: result.message || "환자 정보 복사에 실패했습니다." });
              }
            } catch (error) {
              console.error("Failed to copy patient:", error);
              ToastManager.show({ content: "환자 정보 복사 중 오류가 발생했습니다." });
            } finally {
              WaitingManager.hide();
            }
          }}
        >
          복사
        </Button>,
      ],
    });
  };

  const handleCopyPatientToMyAccount = async () => {
    if (!patientSnap) {
      ToastManager.show({ content: "환자 정보가 없습니다." });
      return;
    }

    ConfirmManager.show({
      title: "환자 정보 복사",
      body: "이 환자의 정보를 내 계정으로 복사하시겠습니까?\n복사된 환자 정보로 새로운 AI 리뷰를 생성할 수 있습니다.",
      buttons: [
        <Button
          key="cancel"
          size="Medium"
          type="Text"
          variant="Outlined"
          color="Secondary"
          onClick={() => {
            ConfirmManager.hide();
          }}
        >
          취소
        </Button>,
        <Button
          key="confirm"
          size="Medium"
          type="Text"
          variant="Contained"
          color="Primary"
          onClick={async () => {
            ConfirmManager.hide();
            try {
              WaitingManager.show({ message: "환자 정보를 복사하는 중..." });
              
              const result = await copyPatient(patientSnap);
              
              if (!result.error && result.data) {
                ToastManager.show({ content: "환자 정보가 성공적으로 복사되었습니다." });
                // 복사된 환자 페이지로 이동
                setTimeout(() => {
                  navigate(`/patients/${result.data.id}`);
                }, 1000);
              } else {
                ToastManager.show({ content: result.message || "환자 정보 복사에 실패했습니다." });
              }
            } catch (error) {
              console.error("Failed to copy patient:", error);
              ToastManager.show({ content: "환자 정보 복사 중 오류가 발생했습니다." });
            } finally {
              WaitingManager.hide();
            }
          }}
        >
          복사
        </Button>,
      ],
    });
  };

  const renderJsonData = (title: string, data: any) => {
    if (!data) return null;
    let jsonString = "";
    try {
      jsonString = JSON.stringify(data, null, 2);
    } catch (error) {
      console.error("Error stringifying JSON for admin viewer:", error);
      jsonString = "데이터를 문자열로 변환하는 중 오류가 발생했습니다.";
    }

    return (
      <div style={{ marginTop: "16px" }}>
        <pre
          style={{
            background: "#f5f5f5",
            padding: "10px",
            borderRadius: "4px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            maxHeight: "50vh",
            overflowY: "auto",
          }}
        >
          {jsonString}
        </pre>
        <button
          onClick={() =>
            copyToClipboard(
              jsonString,
              `'${title}' 내용이 클립보드에 복사되었습니다.`,
              `'${title}' 내용 복사에 실패했습니다.`
            )
          }
          style={{
            marginTop: "10px",
            padding: "8px 12px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer",
            background: "#e9e9e9",
          }}
        >
          Copy {title}
        </button>
      </div>
    );
  };

  const renderTabButton = (tabId: TabId, label: string, data: any) => {
    if (
      !data &&
      !(tabId === TAB_IDS.ACTUAL_PROMPT && actualPrompt) &&
      !(tabId === TAB_IDS.AI_RESPONSE && reviewResult) &&
      !(tabId === TAB_IDS.REASONING_LOG && reasoningLog)
    )
      return null;
    return (
      <button
        key={tabId}
        onClick={() => setActiveTab(tabId)}
        style={{
          padding: "8px 12px",
          marginRight: "8px",
          border: "1px solid #ccc",
          borderRadius: "4px 4px 0 0",
          background: activeTab === tabId ? "#e0e0e0" : "#f9f9f9",
          cursor: "pointer",
          fontWeight: activeTab === tabId ? "bold" : "normal",
        }}
      >
        {label}
      </button>
    );
  };

  if (activeTab === null) {
    if (request) setActiveTab(TAB_IDS.REQUEST);
    else if (patientSnap) setActiveTab(TAB_IDS.PATIENT_SNAP);
    else if (referenceSnap) setActiveTab(TAB_IDS.REFERENCE_SNAP);
    else if (analysis) setActiveTab(TAB_IDS.ANALYSIS);
    else if (actualPrompt) setActiveTab(TAB_IDS.ACTUAL_PROMPT);
    else if (reviewResult) setActiveTab(TAB_IDS.AI_RESPONSE);
    else if (reasoningLog) setActiveTab(TAB_IDS.REASONING_LOG);
  }

  return (
    <div
      style={{
        padding: "20px",
        maxHeight: "95vh",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <h2 style={{ fontSize: "20px", fontWeight: "bold", margin: 0 }}>
          AI 리뷰 상세 정보 (관리자용)
        </h2>
        <Flex gap="8px" customStyle={{ marginRight: "20px" }}>
          <Button
            type="Text"
            variant="Outlined"
            color="Primary"
            size="Small"
            onClick={() => {
              if (patientSnap) {
                openReportInNewWindow(patientSnap, review, patientSnap.consultationNotes);
              }
            }}
          >
            🪟 새창에서 보기
          </Button>
          <Button
            type="Text"
            variant="Outlined"
            color="Secondary"
            size="Small"
            onClick={onRetryLLM}
          >
            다시분석
          </Button>
          <Button
            type="Icon"
            variant="Outlined"
            color="Secondary"
            size="Small"
            onClick={onRefresh}
            icon="Refresh"
          />
          <Button
            type="Icon"
            variant="Outlined"
            color="Negative"
            size="Small"
            onClick={onDeleteReview}
            icon="Trash"
          />
        </Flex>
      </div>

      <div style={{ marginBottom: "16px", borderBottom: "1px solid #ccc", flexShrink: 0 }}>
        {renderTabButton(TAB_IDS.REQUEST, "AI 리뷰 요청", request)}
        {renderTabButton(TAB_IDS.PATIENT_SNAP, "환자 정보 스냅샷", patientSnap)}
        {renderTabButton(TAB_IDS.REFERENCE_SNAP, "참조 데이터 스냅샷", referenceSnap)}
        {renderTabButton(TAB_IDS.ANALYSIS, "성능 분석 정보", analysis)}
        {renderTabButton(TAB_IDS.ACTUAL_PROMPT, "실제 프롬프트", actualPrompt)}
        {renderTabButton(TAB_IDS.AI_RESPONSE, "실제 AI 답변", reviewResult)}
        {renderTabButton(TAB_IDS.REASONING_LOG, "Reasoning Log", reasoningLog)}
        {patientSnap && (
          <Flex gap="8px" customStyle={{ marginLeft: "auto" }}>
            <Button
              type="Text"
              variant="Contained"
              color="Primary"
              size="Small"
              onClick={handleCopyPatientToMyAccount}
            >
              내 계정으로 복사
            </Button>
            <Button
              type="Text"
              variant="Outlined"
              color="Primary"
              size="Small"
              onClick={() => setShowUserSelectModal(true)}
            >
              다른 계정으로 복사
            </Button>
          </Flex>
        )}
      </div>

      <div style={{ flexGrow: 1, overflowY: "auto" }}>
        {activeTab === TAB_IDS.REQUEST && renderJsonData("AI 리뷰 요청", request)}
        {activeTab === TAB_IDS.PATIENT_SNAP &&
          renderJsonData("환자 정보 스냅샷", patientSnap)}
        {activeTab === TAB_IDS.REFERENCE_SNAP &&
          renderJsonData("참조 데이터 스냅샷", referenceSnap)}
        {activeTab === TAB_IDS.ANALYSIS &&
          renderJsonData("성능 분석 정보", analysis)}
        {activeTab === TAB_IDS.ACTUAL_PROMPT && actualPrompt && (
          <div style={{ marginTop: "16px" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginBottom: "10px" }}>
              <button
                onClick={() =>
                  copyToClipboard(
                    actualPrompt,
                    "실제 프롬프트 전체가 클립보드에 복사되었습니다.",
                    "실제 프롬프트 복사에 실패했습니다."
                  )
                }
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  cursor: "pointer",
                  background: "#e9e9e9",
                }}
              >
                Copy 전체 프롬프트
              </button>
              {promptSections.some((section) => section.key === "system") && (
                <button
                  onClick={() =>
                    copyToClipboard(
                      promptSections
                        .filter((section) => section.key !== "system")
                        .map((section) => section.content)
                        .join("\n"),
                      "시스템 프롬프트를 제외한 내용이 클립보드에 복사되었습니다.",
                      "복사에 실패했습니다."
                    )
                  }
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    cursor: "pointer",
                    background: "#e9e9e9",
                  }}
                >
                  Copy 시스템 제외
                </button>
              )}
            </div>
            {promptSections.map((section, idx) => {
              // 동일 pane이 비연속으로 중복될 수 있어 인덱스 결합으로 고유 키 보장
              const uid = `${section.key}-${idx}`;
              // 단일(full) 폴백은 기본 펼침, 다중 분할은 기본 접힘(대용량 렌더 회피)
              const isOpen =
                promptSections.length === 1
                  ? expandedSections[uid] !== false
                  : !!expandedSections[uid];
              return (
                <div
                  key={uid}
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    marginBottom: "8px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    onClick={() => toggleSection(uid, isOpen)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 12px",
                      background: "#f0f0f0",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <span style={{ fontWeight: "bold" }}>
                      {isOpen ? "▼" : "▶"} {section.label}
                      <span style={{ marginLeft: "8px", fontWeight: "normal", color: "#888", fontSize: "12px" }}>
                        {section.lineCount}줄
                      </span>
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(
                          section.content,
                          `'${section.label}' 내용이 클립보드에 복사되었습니다.`,
                          `'${section.label}' 내용 복사에 실패했습니다.`
                        );
                      }}
                      style={{
                        padding: "4px 10px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        cursor: "pointer",
                        background: "#e9e9e9",
                        fontSize: "12px",
                      }}
                    >
                      Copy
                    </button>
                  </div>
                  {isOpen && (
                    <pre
                      style={{
                        margin: 0,
                        background: "#f5f5f5",
                        padding: "10px",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        maxHeight: "60vh",
                        overflowY: "auto",
                      }}
                    >
                      {section.content}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {activeTab === TAB_IDS.AI_RESPONSE && reviewResult && (
          <div style={{ marginTop: "16px" }}>
            <pre
              style={{
                background: "#f5f5f5",
                padding: "10px",
                borderRadius: "4px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                maxHeight: "70vh",
                overflowY: "auto",
              }}
            >
              {reviewResult}
            </pre>
            <button
              onClick={() =>
                copyToClipboard(
                  reviewResult,
                  "실제 AI 답변 내용이 클립보드에 복사되었습니다.",
                  "실제 AI 답변 내용 복사에 실패했습니다."
                )
              }
              style={{
                marginTop: "10px",
                padding: "8px 12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "pointer",
                background: "#e9e9e9",
              }}
            >
              Copy 실제 AI 답변
            </button>
          </div>
        )}
        {activeTab === TAB_IDS.REASONING_LOG && reasoningLog && (
          <div style={{ marginTop: "16px" }}>
            <pre
              style={{
                background: "#f5f5f5",
                padding: "10px",
                borderRadius: "4px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                maxHeight: "70vh",
                overflowY: "auto",
              }}
            >
              {reasoningLog}
            </pre>
            <button
              onClick={() =>
                copyToClipboard(
                  reasoningLog,
                  "Reasoning Log 내용이 클립보드에 복사되었습니다.",
                  "Reasoning Log 내용 복사에 실패했습니다."
                )
              }
              style={{
                marginTop: "10px",
                padding: "8px 12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "pointer",
                background: "#e9e9e9",
              }}
            >
              Copy Reasoning Log
            </button>
          </div>
        )}
      </div>

      {/* 사용자 선택 모달 */}
      {showUserSelectModal && (
        <UserSelectModal onClick={() => setShowUserSelectModal(false)}>
          <UserSelectContent onClick={(e) => e.stopPropagation()}>
            <UserSelectHeader>
              <UserSelectTitle>다른 계정으로 환자 복사</UserSelectTitle>
              <CloseButton onClick={() => setShowUserSelectModal(false)}>×</CloseButton>
            </UserSelectHeader>

            <SectionLabel>자주 사용하는 계정</SectionLabel>
            <UserList>
              {PRESET_USERS.map((user) => (
                <UserItem
                  key={user.id}
                  $isInstitution={user.type === "institution"}
                  onClick={() => handleCopyToSpecificUser(user.id, user.name)}
                >
                  <UserName>{user.name}</UserName>
                  <UserType $type={user.type}>
                    {user.type === "institution" ? "기관" : "관리자"}
                  </UserType>
                </UserItem>
              ))}
            </UserList>

            <SectionLabel>다른 사용자 찾기</SectionLabel>
            <Input
              type="text"
              placeholder="이름, 이메일, 면허번호로 검색 (2자 이상)"
              value={searchQuery}
              onChange={(e) => handleSearchUsers(e.target.value)}
            />
            {isSearching && (
              <div style={{ padding: "8px", color: COLOR_SYSTEM.get("Gray")?.[500], fontSize: "13px" }}>
                검색 중...
              </div>
            )}
            {searchResults.length > 0 && (
              <SearchResultList>
                {searchResults.map((user) => (
                  <UserItem
                    key={user.id}
                    onClick={() => handleCopyToSpecificUser(user.id, user.name)}
                  >
                    <UserName>{user.name}</UserName>
                    <span style={{ fontSize: "12px", color: COLOR_SYSTEM.get("Gray")?.[500] }}>
                      {user.email} {user.license && `| ${user.license}`}
                    </span>
                  </UserItem>
                ))}
              </SearchResultList>
            )}
            {searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && (
              <div style={{ padding: "8px", color: COLOR_SYSTEM.get("Gray")?.[500], fontSize: "13px" }}>
                검색 결과가 없습니다.
              </div>
            )}
          </UserSelectContent>
        </UserSelectModal>
      )}
    </div>
  );
};

export default AIReviewDetailAdminViewerTemplate;
